/* Rates are interval averages from cumulative counters, positioned by time. */
import React, { useId, useMemo, useState } from "react";
import "./FlowEvidence.css";

const clock = (ms) => new Date(ms).toLocaleTimeString(undefined, {
  hour: "2-digit", minute: "2-digit", second: "2-digit",
});
const timestamp = (at) => at == null || at === "" ? NaN : new Date(at).getTime();
const counter = (value) => Number.isFinite(value) && value >= 0;
const rate = (value) => value > 0 && value < 0.01 ? "<0.01" : Number(value.toFixed(2)).toLocaleString();

export function sampleRates(samples) {
  const rows = [];
  let omitted = 0;
  let group = 0;
  if (!Array.isArray(samples)) return { rows, omitted };
  for (let i = 1; i < samples.length; i += 1) {
    const a = samples[i - 1];
    const b = samples[i];
    const start = timestamp(a?.at);
    const at = timestamp(b?.at);
    const seconds = (at - start) / 1000;
    const valid = Number.isFinite(seconds) && seconds > 0
      && [a?.fwd_pkts, a?.rev_pkts, b?.fwd_pkts, b?.rev_pkts].every(counter)
      && b.fwd_pkts >= a.fwd_pkts && b.rev_pkts >= a.rev_pkts
      && (!rows.length || start >= rows[rows.length - 1].at);
    if (!valid) {
      omitted += 1;
      group += 1;
      continue;
    }
    rows.push({ start, at, seconds, out: (b.fwd_pkts - a.fwd_pkts) / seconds,
      back: (b.rev_pkts - a.rev_pkts) / seconds, group });
  }
  return { rows, omitted };
}

export default function StallChart({ samples, stalledAt, interval }) {
  const id = useId();
  const [selection, setSelection] = useState(null);
  const { rows, omitted } = useMemo(() => sampleRates(samples), [samples]);
  if (!rows.length) {
    return <p className="sc-empty">Traffic chart unavailable: at least two valid, increasing samples are needed.</p>;
  }

  const W = 760;
  const H = 212;
  const padL = 48;
  const padR = 16;
  const padT = 40;
  const padB = 28;
  const iw = W - padL - padR;
  const ih = H - padT - padB;
  const t0 = rows[0].start;
  const t1 = rows[rows.length - 1].at;
  const peak = Math.max(1, ...rows.flatMap((r) => [r.out, r.back]));
  const top = peak <= 5 ? Math.ceil(peak) : Math.ceil(peak / 5) * 5;
  const x = (at) => padL + ((at - t0) / (t1 - t0)) * iw;
  const y = (value) => padT + ih - (value / top) * ih;
  const cut = timestamp(stalledAt);
  const showCut = Number.isFinite(cut) && cut >= t0 && cut <= t1;
  const cutX = showCut ? x(cut) : null;
  const selected = selection == null ? rows.length - 1 : Math.max(0, Math.min(rows.length - 1, selection));
  const at = rows[selected];
  const sampleText = `${clock(at.start)} to ${clock(at.at)}: ${rate(at.out)} packets per second from the device, ${rate(at.back)} back to it`;
  const groups = rows.reduce((parts, row) => {
    if (!parts.length || parts[parts.length - 1][0].group !== row.group) parts.push([]);
    parts[parts.length - 1].push(row);
    return parts;
  }, []);
  const line = (part, key) => part.map((r, i) => `${i ? "L" : "M"} ${x(r.start)},${y(r[key])} L ${x(r.at)},${y(r[key])}`).join(" ");
  const area = (part, key) => `${line(part, key)} L ${x(part[part.length - 1].at)},${y(0)} L ${x(part[0].start)},${y(0)} Z`;
  const inspect = (event) => {
    const box = event.currentTarget.getBoundingClientRect();
    if (!box.width) return;
    const time = t0 + ((((event.clientX - box.left) / box.width) * W - padL) / iw) * (t1 - t0);
    const containing = rows.findIndex((row) => time >= row.start && time <= row.at);
    if (containing >= 0) {
      setSelection(containing);
      return;
    }
    let closest = 0;
    rows.forEach((row, i) => {
      if (Math.abs(row.at - time) < Math.abs(rows[closest].at - time)) closest = i;
    });
    setSelection(closest);
  };

  return (
    <figure className="sc flow-evidence-chart" aria-labelledby={`${id}-title`}>
      <figcaption className="sc-head" id={`${id}-title`}>
        <span className="sc-title">What the connection was doing, second by second</span>
        <span className="sc-legend">
          <span><i className="sc-key out" />Sent by the device</span>
          <span><i className="sc-key back" />Answers coming back</span>
        </span>
      </figcaption>
      <div className="fe-chart-scroll" role="region" aria-label="Traffic chart, scroll horizontally on small screens" tabIndex={0}>
        <svg viewBox={`0 0 ${W} ${H}`} className="sc-svg" role="img"
          aria-label={`Packets per second from ${clock(t0)} to ${clock(t1)}.${showCut ? ` Stall recorded at ${clock(cut)}.` : ""} Use the sample selector or sample values table for exact rates.`}
          onPointerMove={inspect} onPointerDown={inspect}>
          {showCut && <rect x={cutX} y={padT} width={padL + iw - cutX} height={ih} className="sc-after" />}
          {[0, 0.5, 1].map((f) => (
            <g key={f}>
              <line x1={padL} x2={padL + iw} y1={y(top * f)} y2={y(top * f)} className="sc-grid" />
              <text x={padL - 8} y={y(top * f) + 4} className="sc-ytick" textAnchor="end">{rate(top * f)}</text>
            </g>
          ))}
          <text x={padL} y="13" className="sc-ytick">packets / second</text>
          {groups.map((part) => (
            <g key={part[0].group}>
              <path d={area(part, "out")} className="sc-area-out" />
              <path d={area(part, "back")} className="sc-area-back" />
              <path d={line(part, "out")} className="sc-line-out" fill="none" />
              <path d={line(part, "back")} className="sc-line-back" fill="none" />
            </g>
          ))}
          {showCut && (
            <>
              <line x1={cutX} x2={cutX} y1={padT - 6} y2={padT + ih} className="sc-cut" />
              <circle cx={cutX} cy={padT - 6} r="3.5" className="sc-cut-dot" />
              <text x={Math.min(padL + iw - 4, Math.max(padL + 4, cutX))} y="25" className="sc-xtick stall"
                textAnchor={cutX > W / 2 ? "end" : "start"}>Froze {clock(cut)}</text>
            </>
          )}
          <line x1={x(at.at)} x2={x(at.at)} y1={padT} y2={padT + ih} className="sc-hoverline" />
          <circle cx={x(at.at)} cy={y(at.out)} r="4" className="sc-dot-out" />
          <circle cx={x(at.at)} cy={y(at.back)} r="4" className="sc-dot-back" />
          <text x={padL} y={H - 8} className="sc-xtick">{clock(t0)}</text>
          <text x={padL + iw} y={H - 8} className="sc-xtick" textAnchor="end">{clock(t1)}</text>
        </svg>
      </div>
      <div className="fe-chart-inspector">
        <div className="fe-chart-reading" aria-live="polite" aria-atomic="true">
          <span>{clock(at.start)} to {clock(at.at)}</span>
          <span><i className="sc-key out" /><strong>{rate(at.out)}</strong> out / s</span>
          <span><i className="sc-key back" /><strong>{rate(at.back)}</strong> replies / s</span>
        </div>
        <label className="fe-sample-control" htmlFor={`${id}-sample`}>
          <span>Inspect sample <b>{selected + 1} / {rows.length}</b></span>
          <input id={`${id}-sample`} type="range" min="0" max={rows.length - 1} step="1" value={selected}
            aria-label="Inspect traffic sample" aria-valuetext={sampleText} disabled={rows.length === 1}
            onChange={(event) => setSelection(Number(event.target.value))} />
        </label>
      </div>
      <p className="sc-foot">Rates average each measured interval. {interval
        ? `Sampled every ${interval}; stall timing is accurate to one sample.`
        : "Sampling interval was not recorded; stall timing is limited by the available samples."}</p>
      {Number.isFinite(cut) && !showCut && <p className="sc-foot">Stall recorded at {clock(cut)}, outside the available chart window.</p>}
      {omitted > 0 && <p className="fe-sample-warning">{omitted} invalid or reset {omitted === 1 ? "interval omitted" : "intervals omitted"}; gaps are not zero traffic.</p>}
      <details className="fe-sample-details">
        <summary>View sample values ({rows.length})</summary>
        <div className="fe-sample-table-wrap" tabIndex={0} role="region" aria-label="Traffic sample values, scroll horizontally on small screens">
          <table className="fe-sample-table">
            <caption>Average packet rates over each measured interval</caption>
            <thead><tr><th scope="col">Interval</th><th scope="col">Seconds</th><th scope="col">From device / s</th><th scope="col">Replies / s</th></tr></thead>
            <tbody>{rows.map((row, i) => (
              <tr key={`${row.start}-${row.at}`} aria-current={i === selected ? "true" : undefined}>
                <th scope="row">{clock(row.start)} to {clock(row.at)}</th>
                <td>{row.seconds.toLocaleString(undefined, { maximumFractionDigits: 3 })}</td>
                <td>{rate(row.out)}</td><td>{rate(row.back)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </details>
    </figure>
  );
}
